import React from 'react';
import { palette, SettingsIcon, SimpleToggle, TertiaryButton, Tip, Typography } from "@dev/ee-components"
import { useDispatch, useSelector } from "react-redux";
import { FormSettingsEnum, ProductCode } from "@dev/ee-api/ee-api-models";
import { useTranslation } from 'react-i18next';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { getPaidNewsletterCreatorStore, TemplateFormPayloadUpdate, TemplateIncrementAutosaveCounter } from "@/store/paidNewsletterCreator/actions";
import { SavedLockedInfo as DoubleOptInUpselling } from '@/components/TemplateEditor/components/SavedLockedInfo';
import { BillingPaths, pathsParams } from '@/services/dictionaries/Paths';
import { HideDialog, ShowDialog } from '@/store/dialog/actions';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { LockIcon } from '@/services/icons/LockedIcon';
import { FormMultiDoubleOptInProps } from "./types";
import { FormMultiDoubleOptInCustomizeWrapper, FormMultiDoubleOptInToggleLabel, FormMultiDoubleOptInToggleWrapper, FormMultiDoubleOptInWrapper } from './styles';
import { useFormMultiDoubleOptInCustomizeModalParams } from '../../hooks/useFormMultiDoubleOptInCustomizeModalParams';

export const FormMultiDoubleOptIn = ({
    blockClientId,
    selectedBlockClientIdRef
}: FormMultiDoubleOptInProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { template } = useSelector(getPaidNewsletterCreatorStore);
    const { isPilotPlan } = useCurrentPlanStatus();
    const historyBackAction = useHistoryBackActionContext();
    const { pushBackCallback } = historyBackAction || {};

    const { getFormMultiDoubleOptInCustomizeModalParams } = useFormMultiDoubleOptInCustomizeModalParams({ historyBackAction });

    const clientId = blockClientId ?? selectedBlockClientIdRef.current;

    const templateForm = template.forms?.find((templateForm) => templateForm.blockClientId === clientId);

    const formPayload = templateForm?.formPayload;
    const isDoubleOptInEnabled = !formPayload?.FormSettings?.includes(FormSettingsEnum.DoubleOptInDisabled);

    const closeModal = (): boolean => {
        dispatch(HideDialog());
        return true;
    };

    const onDoubleOptInChange = (e) => {
        const isEnabled = e.target.checked;
        if (!isEnabled && isDoubleOptInEnabled) {
            dispatch(TemplateFormPayloadUpdate( clientId, { 
                FormSettings: [...(formPayload?.FormSettings?.filter((item) => item !== FormSettingsEnum.AddDoubleOptIn) ?? []), FormSettingsEnum.DoubleOptInDisabled],
                DoubleOptInPage: null,
            } ));
        } else {
            dispatch(TemplateFormPayloadUpdate( clientId, { 
                FormSettings: [...(formPayload?.FormSettings?.filter((item) => item !== FormSettingsEnum.DoubleOptInDisabled) ?? []), FormSettingsEnum.AddDoubleOptIn],
                DoubleOptInPage: null,
            } ));
        }
        dispatch(TemplateIncrementAutosaveCounter());
    };

    const onCustomizeSettingsClick = (): void => {
        pushBackCallback(closeModal);
        dispatch(ShowDialog(getFormMultiDoubleOptInCustomizeModalParams(clientId, formPayload)));
    }

    const label = <FormMultiDoubleOptInToggleLabel><Typography variant="h4">{t('senddoubleoptinconfirmationemail')}</Typography></FormMultiDoubleOptInToggleLabel>

    const clickUpgradeBtn = (): void => {
        window.location.href = `${location.origin}${BillingPaths.BillingPlanList.replace(
            pathsParams.productCode,
            ProductCode.Marketing.toLowerCase()
        )}`;
    };

    if (isPilotPlan) {
        return (
            <FormMultiDoubleOptInWrapper>
                <DoubleOptInUpselling
                    isSidebar={false}
                    isFullWidth
                    title={{ text: t('doubleoptinconfirmation'), icon: <LockIcon width={15} height={15} /> }}
                    body={{ subtitle: t('doubleoptinupselling_subtitle'), text: t('doubleoptinupselling_desc') }}
                    button={{
                        startIcon: <LockIcon width={20} height={20} />,
                        text: t('upgradetounlock'),
                        onClick: clickUpgradeBtn,
                    }}
                />
            </FormMultiDoubleOptInWrapper>
        )
    }

    return (
        <FormMultiDoubleOptInWrapper>
            <FormMultiDoubleOptInToggleWrapper>
                <SimpleToggle
                    id="doubleoptin-toggle"
                    value="doubleoptin"
                    label={label}
                    checked={isDoubleOptInEnabled}
                    onChange={onDoubleOptInChange}
                />
                <Tip id="doubleoptin-toggle-tip">{t('doubleoptintip')}</Tip>
            </FormMultiDoubleOptInToggleWrapper>
            {isDoubleOptInEnabled && (
                <FormMultiDoubleOptInCustomizeWrapper>
                <TertiaryButton
                    startIcon={<SettingsIcon color={palette.indigo[6]} />}
                    onClick={onCustomizeSettingsClick}
                    text={t('customizesettings')}
            /></FormMultiDoubleOptInCustomizeWrapper>)}
        </FormMultiDoubleOptInWrapper>
    )
};
